/**
 * Zapis odpowiedzi API jako pliku do pobrania.
 *
 * DLACZEGO OSOBNY PLIK
 * --------------------
 * Ten sam blok (createObjectURL -> <a download> -> click) wystepuje w
 * repozytorium 15 razy, w `vehicles.service.ts` i `statistics.service.ts`.
 * Zadne z tych 15 wystapien nie zwalnia adresu blob ani nie usuwa
 * dopisanego `<a>` z dokumentu. Skutek: kazdy eksport zostawia w `<body>`
 * ukryty odnosnik, a zawartosc pliku (PDF potrafi miec kilka MB) zostaje
 * przypieta w pamieci karty do jej zamkniecia.
 *
 * WERYFIKACJA TRESCI
 * ------------------
 * Wazniejszy powod: API potrafi odpowiedziec kodem 200 i trescia bledu
 * zamiast pliku. Potwierdzone 2026-09-16 na eksporcie PDF z telemetrii —
 * pobrany plik mial 77 bajtow i zawieral:
 *
 *     <strong>TCPDF ERROR: </strong>Could not include font definition file: segoeui
 *
 * Przegladarka zapisala to jako `.pdf`, a czytnik PDF pokazal „Nie udalo
 * sie wczytac dokumentu PDF". Blad byl po stronie serwera, ale komunikat,
 * ktory zobaczyl uzytkownik, nie mial z nim nic wspolnego. Przy
 * `responseType: "blob"` axios nie rzuca wyjatkiem, bo status to 200 —
 * sprawdzenie musi byc tutaj.
 */

/** Pierwsze bajty poprawnego PDF-a. */
const NAGLOWEK_PDF = "%PDF-";

async function poczatekTekstem(blob: Blob, ile: number): Promise<string> {
    const kawalek = blob.slice(0, ile);
    // FileReader, nie Blob.text() — aplikacja celuje w przegladarki,
    // w ktorych Blob.text() jeszcze nie istnieje.
    return new Promise((zwroc) => {
        const czytnik = new FileReader();
        czytnik.onload = () => zwroc(String(czytnik.result || ""));
        czytnik.onerror = () => zwroc("");
        czytnik.readAsText(kawalek);
    });
}

/**
 * Sprawdza, czy odpowiedz naprawde jest PDF-em. Gdy nie jest — odrzuca
 * obietnice z trescia, ktora przyszla z serwera (obcieta i bez znacznikow
 * HTML), zeby wywolujacy mogl pokazac prawdziwy powod.
 */
async function sprawdzPdf(blob: Blob): Promise<void> {
    const poczatek = await poczatekTekstem(blob, 1024);
    if (poczatek.startsWith(NAGLOWEK_PDF)) return;

    const powod = poczatek
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 300);

    throw new Error(powod || "Serwer nie zwrocil pliku PDF.");
}

/**
 * Podaje uzytkownikowi plik do pobrania i po sobie sprzata.
 *
 * @param blob      tresc pliku
 * @param nazwa     nazwa pliku wraz z rozszerzeniem
 */
function zapiszPlik(blob: Blob, nazwa: string): void {
    const url = window.URL.createObjectURL(blob);
    const odnosnik = document.createElement("a");
    odnosnik.href = url;
    odnosnik.setAttribute("download", nazwa);
    odnosnik.style.display = "none";
    document.body.appendChild(odnosnik);
    odnosnik.click();
    // Sprzatanie: bez tego kazdy eksport zostawia odnosnik w dokumencie
    // i przypieta w pamieci kopie pliku.
    document.body.removeChild(odnosnik);
    window.URL.revokeObjectURL(url);
}

/**
 * Zapisuje odpowiedz jako PDF, ale tylko jesli to faktycznie PDF.
 * W przeciwnym razie odrzuca obietnice trescia bledu z serwera.
 */
async function zapiszPdf(dane: any, nazwa: string): Promise<void> {
    const blob = new Blob([dane], { type: "application/pdf" });
    await sprawdzPdf(blob);
    zapiszPlik(blob, nazwa);
}

/*
 * NAZWA PLIKU Z NAGLOWKA `Content-Disposition`.
 *
 * `zapiszPlik` wymaga nazwy podanej wprost i to jest dobre dla PDF-ow,
 * gdzie nazwe skladamy sami z danych pojazdu. Eksporty CSV i XLSX nazywa
 * jednak serwer, a odczyt tej nazwy wygladal tak, w SZESCIU kopiach:
 *
 *     response.headers['content-disposition'].split(" ", 2)[1].split("=", 2)
 *
 * Dwie osobne drogi do `TypeError`: brak naglowka (serwer nie wysyla go
 * przy bledzie, a przy zadaniach miedzy zrodlami przegladarka udostepnia
 * tylko naglowki z `Access-Control-Expose-Headers`) oraz zapis bez spacji,
 * `attachment;filename=x`, rownie poprawny jak ze spacja.
 */
function odkoduj(wartosc: string): string {
    try { return decodeURIComponent(wartosc); } catch (e) { return wartosc; }
}

function nazwaZNaglowka(naglowek: any, zapasowa: string): string {
    if (!naglowek || typeof naglowek !== "string") return zapasowa;

    /*
     * `filename*` ma pierwszenstwo nad `filename` (RFC 6266). Jego wartosc
     * to `UTF-8''nazwa` — przedrostek z zestawem znakow i jezykiem odcinamy
     * po pierwszym `''`, co dziala dla dowolnego zestawu, nie tylko UTF-8.
     */
    const zGwiazdka = /filename\*\s*=\s*([^;]+)/i.exec(naglowek);
    if (zGwiazdka && zGwiazdka[1]) {
        let wartosc = zGwiazdka[1].trim();
        const koniecPrzedrostka = wartosc.indexOf("''");
        if (koniecPrzedrostka >= 0) wartosc = wartosc.slice(koniecPrzedrostka + 2);
        if (wartosc) return odkoduj(wartosc);
    }

    const zwykly = /filename\s*=\s*"?([^";]+)"?/i.exec(naglowek);
    if (zwykly && zwykly[1] && zwykly[1].trim()) return odkoduj(zwykly[1].trim());

    return zapasowa;
}

/** Rozszerzenie z adresu zadania — tylko gdy brakuje naglowka. */
function rozszerzenieZAdresu(adres: any): string {
    const a = typeof adres === "string" ? adres.toLowerCase() : "";
    if (a.indexOf("excel") >= 0) return ".xlsx";
    if (a.indexOf("csv") >= 0) return ".csv";
    if (a.indexOf("pdf") >= 0) return ".pdf";
    return "";
}

/**
 * Zapisuje odpowiedz axiosa jako plik, biorac nazwe z naglowka.
 *
 * NIE lapie bledow — celowo. Warstwa uslugowa rzuca, a interfejs nazywa
 * powod uzytkownikowi (`utils/pobieranie.ts`). Lapanie tutaj odebraloby
 * przyciskowi jedyne zrodlo informacji.
 */
function zapiszOdpowiedz(odpowiedz: any): void {
    const naglowki: any = odpowiedz && odpowiedz.headers ? odpowiedz.headers : {};
    const adres: any = odpowiedz && odpowiedz.config ? odpowiedz.config.url : "";
    const zapasowa = "eksport" + rozszerzenieZAdresu(adres);
    zapiszPlik(new Blob([odpowiedz.data]), nazwaZNaglowka(naglowki["content-disposition"], zapasowa));
}

export { zapiszPlik, zapiszPdf, sprawdzPdf, zapiszOdpowiedz, nazwaZNaglowka };
